import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
    FaStore, FaShoppingCart, FaSearch, FaLock, FaTimes, FaBoxOpen, FaShippingFast,
    FaChevronLeft, FaChevronRight, FaStar, FaRegStar, FaTrash, FaComments,
    FaHeart, FaRegHeart, FaShieldAlt, FaTruck, FaUndo, FaHeadset, FaFire,
    FaBolt, FaThLarge, FaThList, FaSortAmountDown, FaCheckCircle, FaTag,
    FaAngleLeft, FaAngleRight, FaAngleDoubleLeft, FaAngleDoubleRight,
    FaShare, FaMinus, FaPlus, FaCheck, FaAward, FaClock
} from 'react-icons/fa';
import {
    getProducts, getCategories, createOrder, getProductImages,
    getProductReviews, getReviewsSummary, getReviewEligibility, createReview, deleteReview
} from '../services/api';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useT } from '../i18n/LanguageContext';
import { gridContainer, gridItem, heroContainer, viewportOnce } from '../components/motionPresets';
import './Products.css';

const T = {
    en: {
        // Hero
        todaysDeals: "Today's deals",
        heroTitle: "Shop electronics & accessories",
        heroSubtitle: "Authentic products. Bulk import pricing. Door-to-door logistics.",
        // Trust strip
        fastDelivery: "Fast delivery",
        fastDeliveryRest: " across Cameroon",
        securePayment: "Secure payment",
        securePaymentRest: " guaranteed",
        easyReturnsTrust: "Easy returns",
        easyReturnsTrustRest: " within 7 days",
        support247: "24/7 support",
        support247Rest: " from real people",
        // Pills
        all: "All",
        // Auth notice
        signIn: "Sign in",
        authNoticeRest: " to add items to cart and make purchases.",
        // Toolbar
        searchPlaceholder: "Search products, brands, categories...",
        sortFeatured: "Featured",
        sortPriceAsc: "Price: Low to High",
        sortPriceDesc: "Price: High to Low",
        sortRating: "Top rated",
        sortName: "Name: A-Z",
        viewMode: "View mode",
        gridView: "Grid view",
        listView: "List view",
        cart: "Cart",
        // Sidebar
        categories: "Categories",
        allProducts: "All Products",
        bulkImport: "Bulk import?",
        bulkImportPromo: "Save up to 45% when ordering 500+ units.",
        getQuote: "Get a quote →",
        // Results info
        showingResults: (from, to, total) => <>Showing <strong>{from}-{to}</strong> of <strong>{total}</strong> products</>,
        inCategory: (cat) => <> in <em>{cat}</em></>,
        show: "Show",
        perPage: "per page",
        clearFilters: "Clear filters",
        // Badges
        soldOut: "Sold out",
        bestseller: "Bestseller",
        newBadge: "New",
        hotDeal: "Hot deal",
        // Card
        removeFromWishlist: "Remove from wishlist",
        addToWishlist: "Add to wishlist",
        toggleWishlist: "Toggle wishlist",
        quickAdd: "Quick add",
        freeDelivery: "Free delivery",
        inStock: "In stock",
        outOfStock: "Out of stock",
        addToCart: "Add to Cart",
        // Empty state
        noProductsFound: "No products found",
        noProductsHint: "Try adjusting your search or filters.",
        resetFilters: "Reset filters",
        // Pagination
        productPagination: "Product pagination",
        firstPage: "First page",
        previousPage: "Previous page",
        prev: "Prev",
        next: "Next",
        nextPage: "Next page",
        lastPage: "Last page",
        pageOf: (page, total) => <>Page <strong>{page}</strong> of <strong>{total}</strong></>,
        // Modal
        wishlist: "Wishlist",
        share: "Share",
        reviewsCount: (n) => `(${n} ${n === 1 ? 'review' : 'reviews'})`,
        verifiedSeller: "Verified seller",
        dealPrice: "DEAL PRICE",
        youSave: (amount) => <>You save <strong>{amount} XAF</strong> on this item</>,
        variantLabel: "Variant: ",
        quantityLabel: "Quantity:",
        decrease: "Decrease",
        increase: "Increase",
        onlyNLeft: (n) => `Only ${n} left`,
        inStockShips: "In stock · ships within 24 hours",
        freeDeliveryFeat: "Free delivery",
        freeDeliveryFeatSub: "On this item",
        authenticGuarantee: "Authentic guarantee",
        authenticGuaranteeSub: "100% genuine",
        easyReturns: "Easy returns",
        easyReturnsSub: "7-day policy",
        // Tabs
        buyFromStore: "Buy from Store",
        importBulk: "Import (Bulk)",
        buyNow: "Buy Now",
        signInToPurchase: "Sign in to Purchase",
        // Import section
        cbmLabel: "CBM (Cubic Meter)",
        cbmNote: "Volume for air/sea freight shipping calculation",
        importIntro: "Save more when you import in bulk. Price per unit decreases with quantity:",
        tableQuantity: "Quantity",
        tablePricePerUnit: "Price / Unit",
        tableDiscount: "Discount",
        tierLabel: (min, max) => max ? `${min} - ${max} units` : `${min}+ units`,
        percentOff: (pct) => `${pct}% off`,
        estimateCost: "Estimate your cost:",
        perUnit: (price) => `(${price} XAF/unit)`,
        totalCbm: (cbm) => `Total CBM: ${cbm} m³`,
        shippingCost: (amount) => `Shipping cost: ${amount} XAF`,
        contactToImport: "Contact Us to Import",
        // Reviews
        customerReviews: "Customer Reviews",
        reviewsCountSlash: (n) => `(${n} ${n === 1 ? 'review' : 'reviews'})`,
        leaveReview: "Leave a review",
        reviewPlaceholder: "Share your experience with this product...",
        submitting: "Submitting...",
        submitReview: "Submit Review",
        failedToSubmitReview: "Failed to submit review",
        reviewGatePurchase: "Only customers who have received this product can leave a review.",
        reviewGateReviewed: "You have reviewed this product.",
        signInToReview: " after purchasing to leave a review.",
        noReviewsYet: "No reviews yet. Be the first to share your experience!",
        deleteReviewConfirm: "Delete your review?",
        deleteReviewTitle: "Delete your review",
        failedToDeleteReview: "Failed to delete review",
        starsAria: (i) => `${i} stars`,
        // Cart drawer
        yourCart: (n) => `Your Cart (${n})`,
        orderSuccess: "Order placed successfully!",
        orderError: "Failed to place order. Please try again.",
        cartEmptyTitle: "Your cart is empty",
        cartEmptyHint: "Browse the store and add items to get started.",
        subtotal: "Subtotal",
        delivery: "Delivery",
        free: "Free",
        total: "Total",
        placingOrder: "Placing order...",
        proceedToCheckout: "Proceed to Checkout",
        remove: "Remove",
    },
    fr: {
        // Hero
        todaysDeals: "Offres du jour",
        heroTitle: "Achetez de l'électronique et des accessoires",
        heroSubtitle: "Produits authentiques. Tarifs d'importation en gros. Logistique porte-à-porte.",
        // Trust strip
        fastDelivery: "Livraison rapide",
        fastDeliveryRest: " partout au Cameroun",
        securePayment: "Paiement sécurisé",
        securePaymentRest: " garanti",
        easyReturnsTrust: "Retours faciles",
        easyReturnsTrustRest: " sous 7 jours",
        support247: "Assistance 24/7",
        support247Rest: " par de vraies personnes",
        // Pills
        all: "Tout",
        // Auth notice
        signIn: "Connectez-vous",
        authNoticeRest: " pour ajouter des articles au panier et effectuer des achats.",
        // Toolbar
        searchPlaceholder: "Rechercher produits, marques, catégories...",
        sortFeatured: "En vedette",
        sortPriceAsc: "Prix : croissant",
        sortPriceDesc: "Prix : décroissant",
        sortRating: "Les mieux notés",
        sortName: "Nom : A-Z",
        viewMode: "Mode d'affichage",
        gridView: "Vue grille",
        listView: "Vue liste",
        cart: "Panier",
        // Sidebar
        categories: "Catégories",
        allProducts: "Tous les produits",
        bulkImport: "Import en gros ?",
        bulkImportPromo: "Économisez jusqu'à 45 % en commandant 500 unités ou plus.",
        getQuote: "Demander un devis →",
        // Results info
        showingResults: (from, to, total) => <>Affichage de <strong>{from}-{to}</strong> sur <strong>{total}</strong> produits</>,
        inCategory: (cat) => <> dans <em>{cat}</em></>,
        show: "Afficher",
        perPage: "par page",
        clearFilters: "Effacer les filtres",
        // Badges
        soldOut: "Épuisé",
        bestseller: "Meilleure vente",
        newBadge: "Nouveau",
        hotDeal: "Bonne affaire",
        // Card
        removeFromWishlist: "Retirer des favoris",
        addToWishlist: "Ajouter aux favoris",
        toggleWishlist: "Basculer les favoris",
        quickAdd: "Ajout rapide",
        freeDelivery: "Livraison gratuite",
        inStock: "En stock",
        outOfStock: "Rupture de stock",
        addToCart: "Ajouter au panier",
        // Empty state
        noProductsFound: "Aucun produit trouvé",
        noProductsHint: "Essayez d'ajuster votre recherche ou vos filtres.",
        resetFilters: "Réinitialiser les filtres",
        // Pagination
        productPagination: "Pagination des produits",
        firstPage: "Première page",
        previousPage: "Page précédente",
        prev: "Préc.",
        next: "Suiv.",
        nextPage: "Page suivante",
        lastPage: "Dernière page",
        pageOf: (page, total) => <>Page <strong>{page}</strong> sur <strong>{total}</strong></>,
        // Modal
        wishlist: "Favoris",
        share: "Partager",
        reviewsCount: (n) => `(${n} avis)`,
        verifiedSeller: "Vendeur vérifié",
        dealPrice: "PRIX PROMO",
        youSave: (amount) => <>Vous économisez <strong>{amount} XAF</strong> sur cet article</>,
        variantLabel: "Variante : ",
        quantityLabel: "Quantité :",
        decrease: "Diminuer",
        increase: "Augmenter",
        onlyNLeft: (n) => `Plus que ${n} en stock`,
        inStockShips: "En stock · expédié sous 24 heures",
        freeDeliveryFeat: "Livraison gratuite",
        freeDeliveryFeatSub: "Sur cet article",
        authenticGuarantee: "Garantie d'authenticité",
        authenticGuaranteeSub: "100 % authentique",
        easyReturns: "Retours faciles",
        easyReturnsSub: "Politique de 7 jours",
        // Tabs
        buyFromStore: "Acheter en boutique",
        importBulk: "Importer (en gros)",
        buyNow: "Acheter maintenant",
        signInToPurchase: "Connectez-vous pour acheter",
        // Import section
        cbmLabel: "CBM (Mètre cube)",
        cbmNote: "Volume pour le calcul du fret aérien/maritime",
        importIntro: "Économisez davantage en important en gros. Le prix unitaire diminue avec la quantité :",
        tableQuantity: "Quantité",
        tablePricePerUnit: "Prix / Unité",
        tableDiscount: "Remise",
        tierLabel: (min, max) => max ? `${min} - ${max} unités` : `${min}+ unités`,
        percentOff: (pct) => `${pct}% de remise`,
        estimateCost: "Estimez votre coût :",
        perUnit: (price) => `(${price} XAF/unité)`,
        totalCbm: (cbm) => `CBM total : ${cbm} m³`,
        shippingCost: (amount) => `Coût d'expédition : ${amount} XAF`,
        contactToImport: "Contactez-nous pour importer",
        // Reviews
        customerReviews: "Avis clients",
        reviewsCountSlash: (n) => `(${n} avis)`,
        leaveReview: "Laisser un avis",
        reviewPlaceholder: "Partagez votre expérience avec ce produit...",
        submitting: "Envoi...",
        submitReview: "Publier l'avis",
        failedToSubmitReview: "Échec de l'envoi de l'avis",
        reviewGatePurchase: "Seuls les clients ayant reçu ce produit peuvent laisser un avis.",
        reviewGateReviewed: "Vous avez évalué ce produit.",
        signInToReview: " après votre achat pour laisser un avis.",
        noReviewsYet: "Aucun avis pour le moment. Soyez le premier à partager votre expérience !",
        deleteReviewConfirm: "Supprimer votre avis ?",
        deleteReviewTitle: "Supprimer votre avis",
        failedToDeleteReview: "Échec de la suppression de l'avis",
        starsAria: (i) => `${i} étoiles`,
        // Cart drawer
        yourCart: (n) => `Votre panier (${n})`,
        orderSuccess: "Commande passée avec succès !",
        orderError: "Échec de la commande. Veuillez réessayer.",
        cartEmptyTitle: "Votre panier est vide",
        cartEmptyHint: "Parcourez la boutique et ajoutez des articles pour commencer.",
        subtotal: "Sous-total",
        delivery: "Livraison",
        free: "Gratuite",
        total: "Total",
        placingOrder: "Commande en cours...",
        proceedToCheckout: "Passer à la caisse",
        remove: "Retirer",
    },
};

function Products() {
    const t = useT(T);
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [activeCategory, setActiveCategory] = useState('All');
    const [searchTerm, setSearchTerm] = useState('');
    const [cart, setCart] = useState([]);
    const [showCart, setShowCart] = useState(false);
    const [orderStatus, setOrderStatus] = useState(null);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [popupTab, setPopupTab] = useState('store');
    const [importQty, setImportQty] = useState(10);
    const [productImages, setProductImages] = useState([]);
    const [activeImageIndex, setActiveImageIndex] = useState(0);
    const [imagesLoading, setImagesLoading] = useState(false);
    const [reviewSummary, setReviewSummary] = useState({});
    const [reviews, setReviews] = useState([]);
    const [reviewStats, setReviewStats] = useState({ avgRating: 0, total: 0 });
    const [eligibility, setEligibility] = useState(null);
    const [reviewForm, setReviewForm] = useState({ rating: 5, comment: '' });
    const [reviewError, setReviewError] = useState(null);
    const [submittingReview, setSubmittingReview] = useState(false);
    const [sortBy, setSortBy] = useState('featured');
    const [viewMode, setViewMode] = useState('grid');
    const [wishlist, setWishlist] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(12);
    const [loading, setLoading] = useState(true);
    const [popupQty, setPopupQty] = useState(1);
    const [shareCopied, setShareCopied] = useState(false);
    const { user } = useAuth();

    useEffect(() => {
        setLoading(true);
        Promise.all([
            getProducts().then(res => setProducts(res.data)).catch(() => {}),
            getCategories().then(res => setCategories(res.data)).catch(() => {}),
            getReviewsSummary().then(res => setReviewSummary(res.data)).catch(() => {})
        ]).finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        setCurrentPage(1);
    }, [activeCategory, searchTerm, sortBy, itemsPerPage]);

    // Make the device/browser back button close the product modal instead of
    // leaving the page. When the modal opens we push a throwaway history entry;
    // pressing back pops it (popstate) and we close the modal. If the modal is
    // closed another way (X button, overlay, action), we pop the entry ourselves
    // so a single back press still works as expected.
    useEffect(() => {
        if (!selectedProduct) return;
        window.history.pushState({ productModal: true }, '');
        const handlePopState = () => setSelectedProduct(null);
        window.addEventListener('popstate', handlePopState);
        return () => {
            window.removeEventListener('popstate', handlePopState);
            if (window.history.state && window.history.state.productModal) {
                window.history.back();
            }
        };
    }, [selectedProduct]);

    const filteredProducts = useMemo(() => {
        let list = products.filter(p => {
            const matchCategory = activeCategory === 'All' || p.category === activeCategory;
            const matchSearch = p.name.toLowerCase().includes(searchTerm.toLowerCase());
            return matchCategory && matchSearch;
        });
        switch (sortBy) {
            case 'price-asc':
                list = [...list].sort((a, b) => Number(a.price) - Number(b.price));
                break;
            case 'price-desc':
                list = [...list].sort((a, b) => Number(b.price) - Number(a.price));
                break;
            case 'name':
                list = [...list].sort((a, b) => a.name.localeCompare(b.name));
                break;
            case 'rating': {
                const score = (id) => reviewSummary[id]?.avgRating || 0;
                list = [...list].sort((a, b) => score(b.id) - score(a.id));
                break;
            }
            default:
                break;
        }
        return list;
    }, [products, activeCategory, searchTerm, sortBy, reviewSummary]);

    const totalPages = Math.max(1, Math.ceil(filteredProducts.length / itemsPerPage));
    const safePage = Math.min(currentPage, totalPages);
    const startIdx = (safePage - 1) * itemsPerPage;
    const paginatedProducts = filteredProducts.slice(startIdx, startIdx + itemsPerPage);

    const getPageNumbers = () => {
        const pages = [];
        const maxVisible = 5;
        if (totalPages <= maxVisible + 2) {
            for (let i = 1; i <= totalPages; i++) pages.push(i);
            return pages;
        }
        pages.push(1);
        let start = Math.max(2, safePage - 1);
        let end = Math.min(totalPages - 1, safePage + 1);
        if (safePage <= 3) end = 4;
        if (safePage >= totalPages - 2) start = totalPages - 3;
        if (start > 2) pages.push('...');
        for (let i = start; i <= end; i++) pages.push(i);
        if (end < totalPages - 1) pages.push('...');
        pages.push(totalPages);
        return pages;
    };

    const goToPage = (page) => {
        const p = Math.max(1, Math.min(totalPages, page));
        setCurrentPage(p);
        const top = document.querySelector('.store-main');
        if (top) top.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    const toggleWishlist = (e, id) => {
        e.stopPropagation();
        setWishlist(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    };

    const addToCart = (product) => {
        setCart(prev => {
            const existing = prev.find(item => item.id === product.id && item.variant === product.variant);
            if (existing) {
                return prev.map(item =>
                    item.id === product.id && item.variant === product.variant
                        ? { ...item, quantity: item.quantity + 1 }
                        : item
                );
            }
            return [...prev, { ...product, quantity: 1 }];
        });
        setShowCart(true);
    };

    const quickAdd = (e, product) => {
        e.stopPropagation();
        addToCart(product);
    };

    const removeFromCart = (productId) => {
        setCart(prev => prev.filter(item => item.id !== productId));
    };

    const updateQty = (productId, delta) => {
        setCart(prev => prev.map(item =>
            item.id === productId
                ? { ...item, quantity: Math.max(1, item.quantity + delta) }
                : item
        ));
    };

    const cartTotal = cart.reduce((sum, item) => sum + Number(item.price) * item.quantity, 0);
    const cartCount = cart.reduce((sum, item) => sum + item.quantity, 0);

    const getImportTiers = (unitPrice) => {
        const price = Number(unitPrice);
        return [
            { min: 10, max: 49, pricePerUnit: Math.round(price * 0.85) },
            { min: 50, max: 99, pricePerUnit: Math.round(price * 0.75) },
            { min: 100, max: 499, pricePerUnit: Math.round(price * 0.65) },
            { min: 500, max: null, pricePerUnit: Math.round(price * 0.55) },
        ];
    };

    const getImportPriceForQty = (unitPrice, qty) => {
        const tiers = getImportTiers(unitPrice);
        for (let i = tiers.length - 1; i >= 0; i--) {
            if (qty >= tiers[i].min) return tiers[i].pricePerUnit;
        }
        return Number(unitPrice);
    };

    const openProductPopup = (product) => {
        setSelectedProduct(product);
        setPopupTab('store');
        setImportQty(10);
        setPopupQty(1);
        setShareCopied(false);
        setActiveImageIndex(0);
        setProductImages([]);
        setImagesLoading(true);
        setReviews([]);
        setReviewStats({ avgRating: 0, total: 0 });
        setEligibility(null);
        setReviewForm({ rating: 5, comment: '' });
        setReviewError(null);
        getProductImages(product.id)
            .then(res => setProductImages(res.data))
            .catch(() => setProductImages([]))
            .finally(() => setImagesLoading(false));
        loadProductReviews(product.id);
        if (user) {
            getReviewEligibility(product.id)
                .then(res => setEligibility(res.data))
                .catch(() => setEligibility(null));
        }
    };

    const loadProductReviews = (productId) => {
        getProductReviews(productId)
            .then(res => {
                setReviews(res.data.reviews);
                setReviewStats({ avgRating: res.data.avgRating, total: res.data.total });
                setReviewSummary(prev => ({
                    ...prev,
                    [productId]: { avgRating: res.data.avgRating, total: res.data.total }
                }));
            })
            .catch(() => {});
    };

    const handleSubmitReview = async (e) => {
        e.preventDefault();
        if (!selectedProduct) return;
        setSubmittingReview(true);
        setReviewError(null);
        try {
            await createReview({
                product_id: selectedProduct.id,
                rating: reviewForm.rating,
                comment: reviewForm.comment
            });
            setReviewForm({ rating: 5, comment: '' });
            loadProductReviews(selectedProduct.id);
            setEligibility(prev => prev ? { ...prev, hasReviewed: true, canReview: false } : prev);
        } catch (err) {
            setReviewError(err.response?.data?.error || t.failedToSubmitReview);
        } finally {
            setSubmittingReview(false);
        }
    };

    const handleDeleteOwnReview = async (reviewId) => {
        if (!window.confirm(t.deleteReviewConfirm)) return;
        try {
            await deleteReview(reviewId);
            loadProductReviews(selectedProduct.id);
            setEligibility(prev => prev ? { ...prev, hasReviewed: false, canReview: prev.hasPurchased } : prev);
        } catch {
            alert(t.failedToDeleteReview);
        }
    };

    const renderStars = (rating, interactive = false, onChange = null) => {
        const stars = [];
        for (let i = 1; i <= 5; i++) {
            const filled = i <= Math.round(rating);
            stars.push(
                interactive ? (
                    <button
                        key={i}
                        type="button"
                        className="star-btn"
                        onClick={() => onChange && onChange(i)}
                        aria-label={t.starsAria(i)}
                    >
                        {i <= rating ? <FaStar className="star filled" /> : <FaRegStar className="star" />}
                    </button>
                ) : (
                    <span key={i} className="star-wrap">
                        {filled ? <FaStar className="star filled" /> : <FaRegStar className="star" />}
                    </span>
                )
            );
        }
        return stars;
    };

    const handleCheckout = async () => {
        if (cart.length === 0) return;
        setOrderStatus('processing');
        try {
            const orderData = {
                items: cart.map(item => ({
                    product_id: item.id,
                    quantity: item.quantity,
                    price: item.price
                }))
            };
            await createOrder(orderData);
            setOrderStatus('success');
            setCart([]);
            setTimeout(() => setOrderStatus(null), 4000);
        } catch {
            setOrderStatus('error');
            setTimeout(() => setOrderStatus(null), 4000);
        }
    };

    const productBadge = (product, idx) => {
        if (product.stock_quantity === 0) return { label: t.soldOut, cls: 'badge-soldout' };
        if (idx % 7 === 0) return { label: t.bestseller, cls: 'badge-best', icon: <FaFire /> };
        if (idx % 5 === 0) return { label: t.newBadge, cls: 'badge-new' };
        if (Number(product.price) < 5000) return { label: t.hotDeal, cls: 'badge-deal', icon: <FaBolt /> };
        return null;
    };

    const fakeOriginalPrice = (price) => Math.round(Number(price) * 1.35);

    return (
        <div className="products-page">
            <section className="page-hero products-hero">
                <motion.div className="hero-inner" initial="hidden" animate="show" variants={heroContainer}>
                    <motion.span className="hero-eyebrow" variants={gridItem}><FaTag /> {t.todaysDeals}</motion.span>
                    <motion.h1 variants={gridItem}>{t.heroTitle}</motion.h1>
                    <motion.p variants={gridItem}>{t.heroSubtitle}</motion.p>
                </motion.div>
            </section>

            <div className="trust-strip">
                <div className="trust-strip-inner">
                    <div className="trust-item"><FaTruck /><span><strong>{t.fastDelivery}</strong>{t.fastDeliveryRest}</span></div>
                    <div className="trust-item"><FaShieldAlt /><span><strong>{t.securePayment}</strong>{t.securePaymentRest}</span></div>
                    <div className="trust-item"><FaUndo /><span><strong>{t.easyReturnsTrust}</strong>{t.easyReturnsTrustRest}</span></div>
                    <div className="trust-item"><FaHeadset /><span><strong>{t.support247}</strong>{t.support247Rest}</span></div>
                </div>
            </div>

            {categories.length > 0 && (
                <div className="quick-pills-wrap">
                    <div className="quick-pills-inner">
                        <button
                            className={`quick-pill ${activeCategory === 'All' ? 'active' : ''}`}
                            onClick={() => setActiveCategory('All')}
                        >
                            <FaStore /> {t.all}
                        </button>
                        {categories.map(cat => (
                            <button
                                key={cat}
                                className={`quick-pill ${activeCategory === cat ? 'active' : ''}`}
                                onClick={() => setActiveCategory(cat)}
                            >
                                {cat}
                            </button>
                        ))}
                    </div>
                </div>
            )}


            <section className="store-section">
                <div className="store-container">
                    {!user && (
                        <div className="auth-notice">
                            <FaLock />
                            <span>
                                <Link to="/login">{t.signIn}</Link>{t.authNoticeRest}
                            </span>
                        </div>
                    )}

                    <div className="store-toolbar">
                        <div className="search-bar">
                            <FaSearch />
                            <input
                                type="text"
                                placeholder={t.searchPlaceholder}
                                value={searchTerm}
                                onChange={e => setSearchTerm(e.target.value)}
                            />
                            {searchTerm && (
                                <button className="search-clear" onClick={() => setSearchTerm('')}>
                                    <FaTimes />
                                </button>
                            )}
                        </div>
                        <div className="toolbar-right">
                            <div className="sort-wrap">
                                <FaSortAmountDown />
                                <select value={sortBy} onChange={e => setSortBy(e.target.value)} className="sort-select">
                                    <option value="featured">{t.sortFeatured}</option>
                                    <option value="price-asc">{t.sortPriceAsc}</option>
                                    <option value="price-desc">{t.sortPriceDesc}</option>
                                    <option value="rating">{t.sortRating}</option>
                                    <option value="name">{t.sortName}</option>
                                </select>
                            </div>
                            <div className="view-toggle" role="group" aria-label={t.viewMode}>
                                <button
                                    className={viewMode === 'grid' ? 'active' : ''}
                                    onClick={() => setViewMode('grid')}
                                    title={t.gridView}
                                    aria-label={t.gridView}
                                >
                                    <FaThLarge />
                                </button>
                                <button
                                    className={viewMode === 'list' ? 'active' : ''}
                                    onClick={() => setViewMode('list')}
                                    title={t.listView}
                                    aria-label={t.listView}
                                >
                                    <FaThList />
                                </button>
                            </div>
                            {user && (
                                <button className="cart-btn" onClick={() => setShowCart(true)}>
                                    <FaShoppingCart />
                                    <span>{t.cart}</span>
                                    {cartCount > 0 && <span className="cart-count-badge">{cartCount}</span>}
                                </button>
                            )}
                        </div>
                    </div>

                    <div className="store-body">
                        <aside className="category-sidebar">
                            <h3 className="sidebar-title">{t.categories}</h3>
                            <ul className="category-list">
                                <li>
                                    <button
                                        className={`cat-sidebar-btn ${activeCategory === 'All' ? 'active' : ''}`}
                                        onClick={() => setActiveCategory('All')}
                                    >
                                        <span>{t.allProducts}</span>
                                        <span className="cat-count">{products.length}</span>
                                    </button>
                                </li>
                                {categories.map(cat => {
                                    const count = products.filter(p => p.category === cat).length;
                                    return (
                                        <li key={cat}>
                                            <button
                                                className={`cat-sidebar-btn ${activeCategory === cat ? 'active' : ''}`}
                                                onClick={() => setActiveCategory(cat)}
                                            >
                                                <span>{cat}</span>
                                                <span className="cat-count">{count}</span>
                                            </button>
                                        </li>
                                    );
                                })}
                            </ul>

                            <div className="sidebar-promo">
                                <FaShippingFast />
                                <h4>{t.bulkImport}</h4>
                                <p>{t.bulkImportPromo}</p>
                                <Link to="/contact" className="sidebar-promo-link">{t.getQuote}</Link>
                            </div>
                        </aside>

                        <div className="store-main">
                            <div className="results-info">
                                <span className="results-count">
                                    {t.showingResults(filteredProducts.length === 0 ? 0 : startIdx + 1, Math.min(startIdx + itemsPerPage, filteredProducts.length), filteredProducts.length)}
                                    {activeCategory !== 'All' && t.inCategory(activeCategory)}
                                </span>
                                <div className="results-info-right">
                                    <label className="per-page-label">
                                        {t.show}
                                        <select
                                            value={itemsPerPage}
                                            onChange={e => setItemsPerPage(Number(e.target.value))}
                                            className="per-page-select"
                                        >
                                            <option value={8}>8</option>
                                            <option value={12}>12</option>
                                            <option value={24}>24</option>
                                            <option value={48}>48</option>
                                        </select>
                                        {t.perPage}
                                    </label>
                                    {(activeCategory !== 'All' || searchTerm) && (
                                        <button
                                            className="clear-filters"
                                            onClick={() => { setActiveCategory('All'); setSearchTerm(''); }}
                                        >
                                            {t.clearFilters}
                                        </button>
                                    )}
                                </div>
                            </div>

                            {loading && (
                                <div className={`products-grid ${viewMode === 'list' ? 'list-view' : ''}`}>
                                    {[...Array(8)].map((_, i) => (
                                        <div key={i} className="product-card skeleton-card">
                                            <div className="skeleton-img" />
                                            <div className="product-info">
                                                <div className="skeleton-line skeleton-line-sm" />
                                                <div className="skeleton-line" />
                                                <div className="skeleton-line skeleton-line-short" />
                                                <div className="skeleton-line skeleton-line-price" />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {!loading && (
                            <motion.div
                                key={safePage}
                                className={`products-grid ${viewMode === 'list' ? 'list-view' : ''}`}
                                variants={gridContainer}
                                initial="hidden"
                                animate="show"
                            >
                                {paginatedProducts.map((product, idx) => {
                                    const summary = reviewSummary[product.id];
                                    const badge = productBadge(product, idx);
                                    const original = fakeOriginalPrice(product.price);
                                    const discountPct = Math.round((1 - Number(product.price) / original) * 100);
                                    const inWishlist = wishlist.includes(product.id);
                                    return (
                                        <motion.div key={product.id} className="product-card" variants={gridItem} onClick={() => openProductPopup(product)}>
                                            <div className="product-img-wrap">
                                                <div className="product-img-placeholder">
                                                    {product.image_url
                                                        ? <img src={product.image_url} alt={product.name} className="product-thumb-img" />
                                                        : <FaStore />}
                                                </div>
                                                {badge && (
                                                    <span className={`product-badge ${badge.cls}`}>
                                                        {badge.icon}{badge.label}
                                                    </span>
                                                )}
                                                <span className="discount-badge">-{discountPct}%</span>
                                                <button
                                                    className={`wishlist-btn ${inWishlist ? 'active' : ''}`}
                                                    onClick={(e) => toggleWishlist(e, product.id)}
                                                    title={inWishlist ? t.removeFromWishlist : t.addToWishlist}
                                                    aria-label={t.toggleWishlist}
                                                >
                                                    {inWishlist ? <FaHeart /> : <FaRegHeart />}
                                                </button>
                                                {user && product.stock_quantity > 0 && (
                                                    <button
                                                        className="quick-add-btn"
                                                        onClick={(e) => quickAdd(e, product)}
                                                    >
                                                        <FaShoppingCart /> {t.quickAdd}
                                                    </button>
                                                )}
                                            </div>
                                            <div className="product-info">
                                                <p className="product-category">{product.category}</p>
                                                <h4>{product.name}</h4>
                                                <div className="card-rating">
                                                    <span className="card-stars">{renderStars(summary?.avgRating || 0)}</span>
                                                    <span className="card-rating-count">
                                                        {summary && summary.total > 0 ? `(${summary.total})` : '(0)'}
                                                    </span>
                                                </div>
                                                <p className="product-desc">{product.description}</p>
                                                <div className="price-row">
                                                    <span className="product-price">{Number(product.price).toLocaleString()} XAF</span>
                                                    <span className="product-price-old">{original.toLocaleString()} XAF</span>
                                                </div>
                                                <div className="product-meta">
                                                    <span className="free-ship"><FaTruck /> {t.freeDelivery}</span>
                                                    <span className={`stock-info ${product.stock_quantity === 0 ? 'oos' : ''}`}>
                                                        {product.stock_quantity > 0
                                                            ? <><FaCheckCircle /> {t.inStock}</>
                                                            : t.outOfStock}
                                                    </span>
                                                </div>
                                                {user && product.stock_quantity > 0 && (
                                                    <button
                                                        className="card-add-mini"
                                                        onClick={(e) => quickAdd(e, product)}
                                                        aria-label={t.addToCart}
                                                        title={t.addToCart}
                                                    >
                                                        <FaShoppingCart />
                                                    </button>
                                                )}
                                            </div>
                                        </motion.div>
                                    );
                                })}
                            </motion.div>
                            )}
                            {!loading && filteredProducts.length === 0 && (
                                <div className="no-products">
                                    <FaSearch />
                                    <h3>{t.noProductsFound}</h3>
                                    <p>{t.noProductsHint}</p>
                                    <button className="btn btn-primary" onClick={() => { setActiveCategory('All'); setSearchTerm(''); }}>
                                        {t.resetFilters}
                                    </button>
                                </div>
                            )}

                            {!loading && totalPages > 1 && (
                                <nav className="pagination" aria-label={t.productPagination}>
                                    <button
                                        className="page-btn page-nav"
                                        onClick={() => goToPage(1)}
                                        disabled={safePage === 1}
                                        aria-label={t.firstPage}
                                    >
                                        <FaAngleDoubleLeft />
                                    </button>
                                    <button
                                        className="page-btn page-nav"
                                        onClick={() => goToPage(safePage - 1)}
                                        disabled={safePage === 1}
                                        aria-label={t.previousPage}
                                    >
                                        <FaAngleLeft /> <span className="page-nav-label">{t.prev}</span>
                                    </button>
                                    <div className="page-numbers">
                                        {getPageNumbers().map((p, i) => (
                                            p === '...' ? (
                                                <span key={`e-${i}`} className="page-ellipsis">…</span>
                                            ) : (
                                                <button
                                                    key={p}
                                                    className={`page-btn page-num ${p === safePage ? 'active' : ''}`}
                                                    onClick={() => goToPage(p)}
                                                    aria-current={p === safePage ? 'page' : undefined}
                                                >
                                                    {p}
                                                </button>
                                            )
                                        ))}
                                    </div>
                                    <button
                                        className="page-btn page-nav"
                                        onClick={() => goToPage(safePage + 1)}
                                        disabled={safePage === totalPages}
                                        aria-label={t.nextPage}
                                    >
                                        <span className="page-nav-label">{t.next}</span> <FaAngleRight />
                                    </button>
                                    <button
                                        className="page-btn page-nav"
                                        onClick={() => goToPage(totalPages)}
                                        disabled={safePage === totalPages}
                                        aria-label={t.lastPage}
                                    >
                                        <FaAngleDoubleRight />
                                    </button>
                                    <span className="page-info">
                                        {t.pageOf(safePage, totalPages)}
                                    </span>
                                </nav>
                            )}
                        </div>
                    </div>

                    {selectedProduct && (() => {
                        const inWishlistModal = wishlist.includes(selectedProduct.id);
                        const originalModal = fakeOriginalPrice(selectedProduct.price);
                        const savings = originalModal - Number(selectedProduct.price);
                        const discountPctModal = Math.round((1 - Number(selectedProduct.price) / originalModal) * 100);
                        const handleShare = async () => {
                            try {
                                await navigator.clipboard.writeText(`${window.location.origin}/products`);
                                setShareCopied(true);
                                setTimeout(() => setShareCopied(false), 2000);
                            } catch {}
                        };
                        return (
                        <div className="popup-overlay" onClick={() => setSelectedProduct(null)}>
                            <div className="popup-modal" onClick={e => e.stopPropagation()}>
                                <button className="popup-close" onClick={() => setSelectedProduct(null)}>
                                    <FaTimes />
                                </button>

                                <div className="popup-header">
                                    <div className="popup-gallery">
                                        {imagesLoading ? (
                                            <div className="popup-img-placeholder">
                                                {selectedProduct.image_url
                                                    ? <img src={selectedProduct.image_url} alt={selectedProduct.name} />
                                                    : <FaStore />}
                                            </div>
                                        ) : productImages.length > 0 ? (
                                            <div className="gallery-layout">
                                                {productImages.length > 1 && (
                                                    <div className="gallery-thumbs-vertical">
                                                        {productImages.map((img, i) => (
                                                            <button
                                                                key={img.id}
                                                                className={`gallery-thumb ${i === activeImageIndex ? 'active' : ''}`}
                                                                onClick={() => setActiveImageIndex(i)}
                                                                onMouseEnter={() => setActiveImageIndex(i)}
                                                                title={img.label}
                                                            >
                                                                <img src={img.image_url} alt={img.label} />
                                                            </button>
                                                        ))}
                                                    </div>
                                                )}
                                                <div className="gallery-main">
                                                    <span className="gallery-discount-tag">-{discountPctModal}%</span>
                                                    <div className="gallery-main-img">
                                                        <img src={productImages[activeImageIndex]?.image_url} alt={productImages[activeImageIndex]?.label} />
                                                    </div>
                                                    <span className="gallery-label">{productImages[activeImageIndex]?.label}</span>
                                                    {productImages.length > 1 && (
                                                        <>
                                                            <button className="gallery-nav gallery-prev" onClick={() => setActiveImageIndex(i => i === 0 ? productImages.length - 1 : i - 1)}>
                                                                <FaChevronLeft />
                                                            </button>
                                                            <button className="gallery-nav gallery-next" onClick={() => setActiveImageIndex(i => i === productImages.length - 1 ? 0 : i + 1)}>
                                                                <FaChevronRight />
                                                            </button>
                                                            <span className="gallery-counter">{activeImageIndex + 1} / {productImages.length}</span>
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="popup-img-placeholder">
                                                <span className="gallery-discount-tag">-{discountPctModal}%</span>
                                                {selectedProduct.image_url
                                                    ? <img src={selectedProduct.image_url} alt={selectedProduct.name} />
                                                    : <FaStore />}
                                            </div>
                                        )}
                                    </div>
                                    <div className="popup-details">
                                        <div className="popup-top-row">
                                            <span className="popup-category">{selectedProduct.category}</span>
                                            <div className="popup-actions-icons">
                                                <button
                                                    className={`popup-icon-btn ${inWishlistModal ? 'active' : ''}`}
                                                    onClick={(e) => toggleWishlist(e, selectedProduct.id)}
                                                    title={t.addToWishlist}
                                                    aria-label={t.wishlist}
                                                >
                                                    {inWishlistModal ? <FaHeart /> : <FaRegHeart />}
                                                </button>
                                                <button
                                                    className="popup-icon-btn"
                                                    onClick={handleShare}
                                                    title={t.share}
                                                    aria-label={t.share}
                                                >
                                                    {shareCopied ? <FaCheck /> : <FaShare />}
                                                </button>
                                            </div>
                                        </div>
                                        <h2>{selectedProduct.name}</h2>
                                        <div className="popup-rating-line">
                                            <span className="summary-stars">{renderStars(reviewStats.avgRating || 0)}</span>
                                            <span className="summary-score">{(reviewStats.avgRating || 0).toFixed(1)}</span>
                                            <span className="summary-count">
                                                {t.reviewsCount(reviewStats.total || 0)}
                                            </span>
                                            <span className="rating-divider">·</span>
                                            <span className="verified-seller"><FaAward /> {t.verifiedSeller}</span>
                                        </div>

                                        <div className="popup-price-card">
                                            <span className="price-tag-label">{t.dealPrice}</span>
                                            <div className="price-line">
                                                <span className="price-value">{Number(selectedProduct.price).toLocaleString()} <span className="price-currency">XAF</span></span>
                                                <span className="price-old">{originalModal.toLocaleString()} XAF</span>
                                                <span className="price-discount-chip">-{discountPctModal}%</span>
                                            </div>
                                            <span className="price-savings">
                                                {t.youSave(savings.toLocaleString())}
                                            </span>
                                        </div>

                                        <p className="popup-desc">{selectedProduct.description}</p>

                                        {productImages.length > 0 && (
                                            <div className="popup-variant-section">
                                                <span className="variant-section-label">
                                                    {t.variantLabel}<strong>{productImages[activeImageIndex]?.label}</strong>
                                                </span>
                                                <div className="variant-chips">
                                                    {productImages.map((img, i) => (
                                                        <button
                                                            key={img.id}
                                                            className={`variant-chip ${i === activeImageIndex ? 'active' : ''}`}
                                                            onClick={() => setActiveImageIndex(i)}
                                                        >
                                                            {img.label}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {popupTab === 'store' && user && selectedProduct.stock_quantity > 0 && (
                                            <div className="popup-qty-section">
                                                <span className="qty-label">{t.quantityLabel}</span>
                                                <div className="popup-qty-control">
                                                    <button
                                                        onClick={() => setPopupQty(q => Math.max(1, q - 1))}
                                                        aria-label={t.decrease}
                                                    >
                                                        <FaMinus />
                                                    </button>
                                                    <input
                                                        type="number"
                                                        min="1"
                                                        max={selectedProduct.stock_quantity}
                                                        value={popupQty}
                                                        onChange={e => setPopupQty(Math.max(1, Math.min(selectedProduct.stock_quantity, parseInt(e.target.value) || 1)))}
                                                    />
                                                    <button
                                                        onClick={() => setPopupQty(q => Math.min(selectedProduct.stock_quantity, q + 1))}
                                                        aria-label={t.increase}
                                                    >
                                                        <FaPlus />
                                                    </button>
                                                </div>
                                                <span className="qty-stock-note">
                                                    <FaClock /> {t.onlyNLeft(selectedProduct.stock_quantity)}
                                                </span>
                                            </div>
                                        )}

                                        <p className={`popup-stock ${selectedProduct.stock_quantity === 0 ? 'oos' : ''}`}>
                                            {selectedProduct.stock_quantity > 0
                                                ? <><FaCheckCircle /> {t.inStockShips}</>
                                                : t.outOfStock}
                                        </p>

                                        <ul className="popup-features">
                                            <li><span className="feat-ic"><FaTruck /></span><div><strong>{t.freeDeliveryFeat}</strong><small>{t.freeDeliveryFeatSub}</small></div></li>
                                            <li><span className="feat-ic"><FaShieldAlt /></span><div><strong>{t.authenticGuarantee}</strong><small>{t.authenticGuaranteeSub}</small></div></li>
                                            <li><span className="feat-ic"><FaUndo /></span><div><strong>{t.easyReturns}</strong><small>{t.easyReturnsSub}</small></div></li>
                                        </ul>
                                    </div>
                                </div>

                                <div className="popup-tabs">
                                    <button
                                        className={`popup-tab ${popupTab === 'store' ? 'active' : ''}`}
                                        onClick={() => setPopupTab('store')}
                                    >
                                        <FaBoxOpen /> {t.buyFromStore}
                                    </button>
                                    <button
                                        className={`popup-tab ${popupTab === 'import' ? 'active' : ''}`}
                                        onClick={() => setPopupTab('import')}
                                    >
                                        <FaShippingFast /> {t.importBulk}
                                    </button>
                                </div>

                                {popupTab === 'store' && (
                                    <div className="popup-tab-content">
                                        <div className="store-action-row">
                                            {user ? (
                                                <>
                                                    <button
                                                        className="popup-action-btn"
                                                        disabled={selectedProduct.stock_quantity === 0}
                                                        onClick={() => {
                                                            const variant = productImages.length > 0 ? productImages[activeImageIndex]?.label : null;
                                                            for (let i = 0; i < popupQty; i++) {
                                                                addToCart({ ...selectedProduct, variant });
                                                            }
                                                            setSelectedProduct(null);
                                                        }}
                                                    >
                                                        <FaShoppingCart /> {t.addToCart}
                                                    </button>
                                                    <button
                                                        className="popup-action-btn popup-buy-now"
                                                        disabled={selectedProduct.stock_quantity === 0}
                                                        onClick={async () => {
                                                            const variant = productImages.length > 0 ? productImages[activeImageIndex]?.label : null;
                                                            for (let i = 0; i < popupQty; i++) {
                                                                addToCart({ ...selectedProduct, variant });
                                                            }
                                                            setSelectedProduct(null);
                                                            setShowCart(true);
                                                        }}
                                                    >
                                                        <FaBolt /> {t.buyNow}
                                                    </button>
                                                </>
                                            ) : (
                                                <Link to="/login" className="popup-action-btn login-to-buy-popup" onClick={() => setSelectedProduct(null)}>
                                                    <FaLock /> {t.signInToPurchase}
                                                </Link>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {popupTab === 'import' && (
                                    <div className="popup-tab-content">
                                        {selectedProduct.cbm && (
                                            <div className="cbm-info">
                                                <span className="cbm-label">{t.cbmLabel}</span>
                                                <span className="cbm-value">{Number(selectedProduct.cbm).toFixed(4)} m³</span>
                                                {selectedProduct.cbm_price && (
                                                    <span className="cbm-price">{Number(selectedProduct.cbm_price).toLocaleString()} XAF / m³</span>
                                                )}
                                                <span className="cbm-note">{t.cbmNote}</span>
                                            </div>
                                        )}
                                        <p className="import-intro">{t.importIntro}</p>
                                        <table className="import-tiers-table">
                                            <thead>
                                                <tr>
                                                    <th>{t.tableQuantity}</th>
                                                    <th>{t.tablePricePerUnit}</th>
                                                    <th>{t.tableDiscount}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {getImportTiers(selectedProduct.price).map((tier, i) => (
                                                    <tr key={i}>
                                                        <td>{t.tierLabel(tier.min, tier.max)}</td>
                                                        <td className="tier-price">{tier.pricePerUnit.toLocaleString()} XAF</td>
                                                        <td className="tier-discount">{t.percentOff(Math.round((1 - tier.pricePerUnit / Number(selectedProduct.price)) * 100))}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>

                                        <div className="import-calculator">
                                            <label>{t.estimateCost}</label>
                                            <div className="calc-row">
                                                <input
                                                    type="number"
                                                    min="1"
                                                    value={importQty}
                                                    onChange={e => setImportQty(Math.max(1, parseInt(e.target.value) || 1))}
                                                    className="qty-input"
                                                />
                                                <span className="calc-result">
                                                    = <strong>{(getImportPriceForQty(selectedProduct.price, importQty) * importQty).toLocaleString()} XAF</strong>
                                                    <small>{t.perUnit(getImportPriceForQty(selectedProduct.price, importQty).toLocaleString())}</small>
                                                    {selectedProduct.cbm && (
                                                        <>
                                                            <small className="calc-cbm">{t.totalCbm((Number(selectedProduct.cbm) * importQty).toFixed(4))}</small>
                                                            {selectedProduct.cbm_price && (
                                                                <small className="calc-cbm">{t.shippingCost((Number(selectedProduct.cbm) * importQty * Number(selectedProduct.cbm_price)).toLocaleString())}</small>
                                                            )}
                                                        </>
                                                    )}
                                                </span>
                                            </div>
                                        </div>

                                        <Link to="/contact" className="popup-action-btn import-cta" onClick={() => setSelectedProduct(null)}>
                                            {t.contactToImport}
                                        </Link>
                                    </div>
                                )}

                                <div className="reviews-section">
                                    <h3 className="reviews-title">
                                        <FaComments /> {t.customerReviews}
                                        {reviewStats.total > 0 && (
                                            <span className="reviews-summary">
                                                <span className="summary-stars">{renderStars(reviewStats.avgRating)}</span>
                                                <span className="summary-score">
                                                    {reviewStats.avgRating.toFixed(1)} / 5
                                                </span>
                                                <span className="summary-count">{t.reviewsCountSlash(reviewStats.total)}</span>
                                            </span>
                                        )}
                                    </h3>

                                    {user && eligibility?.canReview && (
                                        <form className="review-form" onSubmit={handleSubmitReview}>
                                            <label className="review-form-label">{t.leaveReview}</label>
                                            <div className="rating-input">
                                                {renderStars(reviewForm.rating, true, (r) => setReviewForm({ ...reviewForm, rating: r }))}
                                            </div>
                                            <textarea
                                                className="review-textarea"
                                                placeholder={t.reviewPlaceholder}
                                                value={reviewForm.comment}
                                                onChange={e => setReviewForm({ ...reviewForm, comment: e.target.value })}
                                                rows="3"
                                                maxLength={1000}
                                            />
                                            {reviewError && <p className="review-error">{reviewError}</p>}
                                            <button
                                                type="submit"
                                                className="popup-action-btn review-submit"
                                                disabled={submittingReview}
                                            >
                                                {submittingReview ? t.submitting : t.submitReview}
                                            </button>
                                        </form>
                                    )}

                                    {user && eligibility && !eligibility.hasPurchased && (
                                        <p className="review-gate-msg">
                                            {t.reviewGatePurchase}
                                        </p>
                                    )}

                                    {user && eligibility?.hasReviewed && (
                                        <p className="review-gate-msg success">{t.reviewGateReviewed}</p>
                                    )}

                                    {!user && (
                                        <p className="review-gate-msg">
                                            <Link to="/login">{t.signIn}</Link>{t.signInToReview}
                                        </p>
                                    )}

                                    <div className="reviews-list">
                                        {reviews.length === 0 && (
                                            <p className="empty-reviews">{t.noReviewsYet}</p>
                                        )}
                                        {reviews.map(r => (
                                            <div key={r.id} className="review-item">
                                                <div className="review-head">
                                                    <div>
                                                        <strong className="review-user">{r.user_name}</strong>
                                                        <span className="review-stars">{renderStars(r.rating)}</span>
                                                    </div>
                                                    <div className="review-meta">
                                                        <span className="review-date">{new Date(r.created_at).toLocaleDateString()}</span>
                                                        {user && r.user_id === user.id && (
                                                            <button
                                                                className="review-delete-btn"
                                                                onClick={() => handleDeleteOwnReview(r.id)}
                                                                title={t.deleteReviewTitle}
                                                            >
                                                                <FaTrash />
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                                {r.comment && <p className="review-comment">{r.comment}</p>}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                        );
                    })()}
                </div>
            </section>

            {user && (
                <>
                    <div className={`cart-drawer-overlay ${showCart ? 'open' : ''}`} onClick={() => setShowCart(false)} />
                    <aside className={`cart-drawer ${showCart ? 'open' : ''}`} aria-hidden={!showCart}>
                        <div className="cart-drawer-head">
                            <h3><FaShoppingCart /> {t.yourCart(cartCount)}</h3>
                            <button className="cart-drawer-close" onClick={() => setShowCart(false)}><FaTimes /></button>
                        </div>
                        <div className="cart-drawer-body">
                            {orderStatus === 'success' && (
                                <div className="order-success"><FaCheckCircle /> {t.orderSuccess}</div>
                            )}
                            {orderStatus === 'error' && (
                                <div className="order-error">{t.orderError}</div>
                            )}
                            {cart.length === 0 && !orderStatus ? (
                                <div className="empty-cart-state">
                                    <FaShoppingCart />
                                    <h4>{t.cartEmptyTitle}</h4>
                                    <p>{t.cartEmptyHint}</p>
                                </div>
                            ) : (
                                cart.map(item => (
                                    <div key={`${item.id}-${item.variant || ''}`} className="cart-item">
                                        <div className="cart-item-thumb">
                                            {item.image_url ? <img src={item.image_url} alt={item.name} /> : <FaStore />}
                                        </div>
                                        <div className="cart-item-info">
                                            <strong>{item.name}</strong>
                                            {item.variant && <span className="cart-variant">{item.variant}</span>}
                                            <span className="cart-item-price">{Number(item.price).toLocaleString()} XAF</span>
                                            <div className="cart-qty">
                                                <button onClick={() => updateQty(item.id, -1)}>−</button>
                                                <span>{item.quantity}</span>
                                                <button onClick={() => updateQty(item.id, 1)}>+</button>
                                            </div>
                                        </div>
                                        <button onClick={() => removeFromCart(item.id)} className="remove-btn" aria-label={t.remove}>
                                            <FaTrash />
                                        </button>
                                    </div>
                                ))
                            )}
                        </div>
                        {cart.length > 0 && (
                            <div className="cart-drawer-foot">
                                <div className="cart-summary-row"><span>{t.subtotal}</span><strong>{cartTotal.toLocaleString()} XAF</strong></div>
                                <div className="cart-summary-row muted"><span>{t.delivery}</span><strong>{t.free}</strong></div>
                                <div className="cart-summary-row total"><span>{t.total}</span><strong>{cartTotal.toLocaleString()} XAF</strong></div>
                                <button
                                    className="btn btn-primary checkout-btn"
                                    onClick={handleCheckout}
                                    disabled={orderStatus === 'processing'}
                                >
                                    {orderStatus === 'processing' ? t.placingOrder : t.proceedToCheckout}
                                </button>
                            </div>
                        )}
                    </aside>
                </>
            )}
        </div>
    );
}

export default Products;
